/**
 * Why a reviewed plan must not be created, or `null` when it may proceed.
 *
 * A plan is configured, reviewed, and only then authorised, so the figures it was built from can
 * go stale in between. Both reasons are checked here rather than at the call site because they
 * are independent — a lapsed discount and a moved balance have nothing to do with each other,
 * and nesting one inside the other silently narrows the guard to the case where both coincide.
 */
import type { ActiveDiscountOffer } from '@ophelos/shared/types/discount-offer';
import { pricedOffersStillLive } from './discount-offers';

export type PlanRefusal = 'offer-expired' | 'balance-changed';

/**
 * @param plan The reviewed plan: the total it was priced at, and the offers (if any) that priced it.
 * @param liveBalance The balance the plan must not exceed — the case's for a case-scoped plan, the
 *   sum of payable debts for an account-scoped one.
 *
 * The balance check is one-directional on purpose. Over-collecting takes money that is not owed;
 * a total *under* the balance is legitimate, because that is exactly what a discount produces.
 * It is also a floor, not a guarantee: `liveBalance` is itself only as fresh as the last fetch,
 * so only the server sees the true figure at creation time.
 */
export function planRefusalReason(
  plan: { totalAmount: number; pricedOfferIds?: string[] },
  liveBalance: number,
  activeOffers: Record<string, ActiveDiscountOffer>,
): PlanRefusal | null {
  // Reported first when both apply: it explains why the price the customer is looking at changed.
  if (!pricedOffersStillLive(plan.pricedOfferIds, activeOffers)) return 'offer-expired';
  if (plan.totalAmount > liveBalance) return 'balance-changed';
  return null;
}
